import type { FormEvent, ReactNode } from 'react'

export interface EntryGridProps {
  title: string
  description?: string
  onSubmit: () => void
  submitLabel: string
  busy?: boolean
  /** Disables submit without the busy label, e.g. for a read-only member. */
  disabled?: boolean
  error?: string | null
  success?: string | null
  children: ReactNode
}

/**
 * The dense entry form the farm fills in at the end of the day.
 *
 * Desktop-first and deliberately plain: the fastest thing to fill in is a row of
 * labelled boxes and one button, not a wizard.
 */
export function EntryGrid({
  title,
  description,
  onSubmit,
  submitLabel,
  busy,
  disabled,
  error,
  success,
  children,
}: EntryGridProps) {
  const handle = (event: FormEvent) => {
    event.preventDefault()
    if (!busy && !disabled) onSubmit()
  }

  return (
    <form className="entry-grid" onSubmit={handle}>
      <div className="entry-head">
        <h3>{title}</h3>
        {description ? <p className="muted">{description}</p> : null}
      </div>

      <div className="entry-cells">{children}</div>

      <div className="entry-actions">
        <button type="submit" disabled={busy || disabled}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {error ? (
          <span role="alert" className="error">
            {error}
          </span>
        ) : null}
        {success ? <span className="success">{success}</span> : null}
      </div>
    </form>
  )
}
